// Libs
import { clone, uniq } from "lodash";
import { useHistory } from "react-router";

// Hooks
import { useAuth } from "src/hooks";

// Types
import { MarketingActionType } from "src/types/enum/MarketingActionType";
import { MarketingActionVariantEnum } from "src/utils/variants";

// Utils
import { useTranslation } from "src/design-system/core/TranslationContext";
import { UserRoleEnum } from "src/types/role";

// Components
import { MarketingActionVariantIcons } from "src/components-ds/displays";
import { Button, ButtonContainer } from "src/design-system/molecules/buttons";
import { ButtonDropdown } from "src/design-system/organisms/buttons";

// TODO : OLD VIEWS IMPORTS
import { track } from "@amplitude/analytics-browser";
import { Tooltip } from "src/design-system/molecules/modals";
import { convertCamelCaseToKebabCase } from "src/utils/caseConverter";
import {
    isSponsoredAdDisabled,
    sponsoredAdChannelDisabledList,
} from "src/views-ds/offer/utils/utils";
import {
    MARKETING_ACTIONS_CREATE_VIEW_TK,
    MARKETING_ACTION_SELECTOR_TRANSLATION_NAMESPACE,
} from "src/views/marketing-actions/components/MarketingActionSelector/config";
import { marketingActionRoutes } from "src/views/marketing-actions/routes";

export function CreateSponsoredAdButton({
    hideShortcutAction = false,
    groupedMoreButtonProps,
    page,
}: {
    hideShortcutAction?: boolean;
    groupedMoreButtonProps?: React.ComponentProps<typeof ButtonDropdown>["moreButtonProps"];
    page?: string;
}): JSX.Element {
    const { t, translateCommon } = useTranslation(
        `${MARKETING_ACTION_SELECTOR_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}`
    );
    const history = useHistory();
    const { user } = useAuth();
    const userChannel = user?.channels?.map((e) => e.source) ?? [];
    const disabled =
        user.channels.length === 0 ||
        user.role === UserRoleEnum.SUPERADMIN ||
        isSponsoredAdDisabled(userChannel);
    const tooltipDisabledText = disabled
        ? t("sponsored-ad-disabled-tooltip-button", {
              platforms: sponsoredAdChannelDisabledList(userChannel, translateCommon),
          })
        : "";

    // CLASSIC BUTTON
    const labelClassicButton = translateCommon(`actions.create-concept`, {
        concept: translateCommon("concepts.an-auto-rule"),
    });
    const handleClickClassicButton = () =>
        history.push(
            marketingActionRoutes.ad({
                variants: [
                    MarketingActionVariantEnum.SponsoredAd,
                    MarketingActionVariantEnum.AutoRule,
                ],
            }),
            {
                previousLocation: clone(history.location),
            }
        );

    // GROUPED BUTTON
    const labelGroupedButton = translateCommon(`actions.create-concept`, {
        concept: translateCommon("concepts.a-sponsored-ad"),
    });

    const handleClickGroupedButtonItem = (variant: MarketingActionVariantEnum) => {
        track("click-button", {
            page: page ?? "unknown-page",
            name: `create-sponsored-ad`,
            actionType: convertCamelCaseToKebabCase(variant),
            MarketingActionType: MarketingActionType.SponsoredAd,
        });
        history.push(
            marketingActionRoutes.offer({
                variants: uniq([MarketingActionVariantEnum.SponsoredAd, variant]),
            }),
            { previousLocation: clone(history.location) }
        );
    };

    const ListVariantOrder = [
        undefined,
        MarketingActionVariantEnum.SponsoredAd,
        MarketingActionVariantEnum.AutoRule,
    ];

    const groupedItemList = ListVariantOrder.map((variant) => {
        return {
            key: variant ?? "title-grouped-items-sponsored-ad",
            icon: variant ? (
                <MarketingActionVariantIcons
                    variants={
                        variant === MarketingActionVariantEnum.SponsoredAd
                            ? [variant, MarketingActionVariantEnum.Classic]
                            : [variant]
                    }
                />
            ) : undefined,
            isGroupTitle: variant === undefined,
            label: variant
                ? translateCommon(`concepts.${variant}`)
                : t(`title`, {
                      concept: translateCommon(
                          `concepts.${MarketingActionVariantEnum.SponsoredAd}`
                      ),
                  }),
            secondaryLabel: variant
                ? t(`${MarketingActionType.SponsoredAd}.item.${variant}.subtitle`)
                : "",
            action: () => (variant ? handleClickGroupedButtonItem(variant) : undefined),
            className: variant ? "fl-c-create-sponsored-ad-button__item" : "",
        };
    });

    return (
        <ButtonContainer>
            {!hideShortcutAction && (
                <Tooltip tooltip={tooltipDisabledText}>
                    <Button
                        variant="outlined"
                        icon="auto-rule"
                        iconSize="s"
                        disabled={disabled}
                        label={labelClassicButton}
                        onClick={handleClickClassicButton}
                    />
                </Tooltip>
            )}

            <Tooltip tooltip={tooltipDisabledText}>
                <ButtonDropdown
                    label={labelGroupedButton}
                    actions={groupedItemList}
                    moreButtonProps={{
                        icon: "add-circle-outline",
                        iconPosition: "left",
                        disabled,
                        ...groupedMoreButtonProps,
                    }}
                    dropdownProps={{ contentClassName: "fl-c-create-sponsored-ad-button" }}
                />
            </Tooltip>
        </ButtonContainer>
    );
}
